import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { logFunnel } from '../lib/funnel';
import {
  X, RotateCcw, Volume2, VolumeX, ArrowRight, Mail, Users, DollarSign,
  Sparkles, CheckCircle, MousePointer2, Send, Bell, Check, Star,
  TrendingUp, Megaphone, UserPlus, Zap
} from 'lucide-react';

// ---------- Animated Scenes ----------
// Each scene returns JSX representing the "screen" shown in the player.
// Captions (bottom subtitle) and optional callout (floating bubble) live in scene meta.

// ===== SIGNATURES DEMO =====
const SignatureScenes = {
  Scene1_Pick: ({ progress }) => (
    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 p-6 overflow-hidden">
      <div className="text-white/70 text-xs mb-3 font-semibold tracking-wider uppercase">Choose Your Template</div>
      <div className="grid grid-cols-3 gap-3">
        {['Modern', 'Luxury Tile', 'Premium Corp'].map((name, i) => {
          const isPicked = progress > 0.55 && i === 1;
          const isHover = progress > 0.25 && progress < 0.55 && i === 1;
          return (
            <div
              key={name}
              className={`relative rounded-lg p-3 border-2 transition-all duration-500 ${
                isPicked ? 'border-emerald-400 bg-emerald-500/10 scale-105 shadow-lg shadow-emerald-500/30' :
                isHover ? 'border-blue-400 bg-blue-500/10 scale-[1.02]' :
                'border-white/10 bg-white/5'
              }`}
            >
              <div className={`h-14 rounded mb-2 ${
                i === 0 ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                i === 1 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                'bg-gradient-to-br from-slate-600 to-slate-800'
              }`} />
              <div className="text-white text-[10px] font-semibold">{name}</div>
              <div className="text-white/50 text-[9px]">Pro</div>
              {isPicked && (
                <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-emerald-400 flex items-center justify-center animate-bounce">
                  <Check className="w-3 h-3 text-slate-900" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {progress > 0.3 && progress < 0.55 && (
        <MousePointer2
          className="absolute w-5 h-5 text-white drop-shadow-lg transition-all duration-300"
          style={{ left: '46%', top: '52%' }}
        />
      )}
    </div>
  ),

  Scene2_Fill: ({ progress }) => {
    const fullName = 'Sarah Johnson';
    const fullTitle = 'Real Estate Agent';
    const fullCompany = 'Premier Realty';
    const nameLen = Math.min(fullName.length, Math.floor(progress * fullName.length * 1.6));
    const titleLen = progress > 0.45 ? Math.min(fullTitle.length, Math.floor((progress - 0.45) * fullTitle.length * 3)) : 0;
    const companyLen = progress > 0.75 ? Math.min(fullCompany.length, Math.floor((progress - 0.75) * fullCompany.length * 4)) : 0;
    return (
      <div className="absolute inset-0 bg-white p-6 overflow-hidden">
        <div className="text-slate-500 text-xs mb-4 font-semibold tracking-wider uppercase">Your Details</div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] text-slate-400 mb-1">Full Name</div>
            <div className="border-2 border-blue-400 rounded-lg px-3 py-2 text-slate-900 text-sm font-medium bg-blue-50/50">
              {fullName.slice(0, nameLen)}<span className="animate-pulse text-blue-500">|</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400 mb-1">Job Title</div>
            <div className={`border-2 rounded-lg px-3 py-2 text-sm ${titleLen > 0 ? 'border-blue-400 bg-blue-50/50 text-slate-900 font-medium' : 'border-slate-200 text-slate-300'}`}>
              {titleLen > 0 ? fullTitle.slice(0, titleLen) : 'e.g., Real Estate Agent'}
              {titleLen > 0 && titleLen < fullTitle.length && <span className="animate-pulse text-blue-500">|</span>}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400 mb-1">Company</div>
            <div className={`border-2 rounded-lg px-3 py-2 text-sm ${companyLen > 0 ? 'border-blue-400 bg-blue-50/50 text-slate-900 font-medium' : 'border-slate-200 text-slate-300'}`}>
              {companyLen > 0 ? fullCompany.slice(0, companyLen) : 'e.g., Premier Realty'}
              {companyLen > 0 && companyLen < fullCompany.length && <span className="animate-pulse text-blue-500">|</span>}
            </div>
          </div>
        </div>
      </div>
    );
  },

  Scene3_AI: ({ progress }) => (
    <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-6 overflow-hidden flex flex-col items-center justify-center">
      <div className="relative">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-pulse shadow-2xl shadow-purple-500/50">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 rounded-full border-4 border-purple-400/30 animate-ping" />
      </div>
      <div className="text-white text-base font-bold mt-5 font-['Outfit']">AI is crafting your signature</div>
      <div className="text-purple-200 text-xs mt-1">Optimized for engagement & conversions</div>
      <div className="mt-5 w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-400 to-pink-400 transition-all duration-200"
          style={{ width: `${Math.min(progress * 100, 100)}%` }}
        />
      </div>
      <div className="text-white/40 text-[10px] mt-2">{Math.min(Math.floor(progress * 100), 100)}%</div>
    </div>
  ),

  Scene4_Result: ({ progress }) => (
    <div className="absolute inset-0 bg-slate-50 p-5 overflow-hidden flex items-center justify-center">
      <div
        className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 w-full max-w-sm transition-all duration-700"
        style={{ transform: progress > 0.1 ? 'scale(1)' : 'scale(0.85)', opacity: progress > 0.1 ? 1 : 0 }}
      >
        <div className="flex gap-3 items-start">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
            SJ
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 text-sm">Sarah Johnson</div>
            <div className="text-amber-600 text-xs font-semibold">Real Estate Agent</div>
            <div className="text-slate-500 text-[11px]">Premier Realty</div>
            <div className="h-px bg-gradient-to-r from-amber-400 to-orange-500 my-2" />
            <div className="flex gap-2 text-[10px] text-slate-600">
              <span>📧 sarah@premier.co</span>
              <span>📱 (555) 010-2024</span>
            </div>
          </div>
        </div>
        {progress > 0.5 && (
          <div className="mt-3 rounded-lg overflow-hidden border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[9px] text-amber-700 font-bold uppercase tracking-wide">Featured Partner</div>
                <div className="text-xs font-semibold text-slate-800">Michael&apos;s Mortgage</div>
              </div>
              <div className="text-[9px] text-amber-700 font-semibold">AD</div>
            </div>
          </div>
        )}
      </div>
    </div>
  ),

  Scene5_Install: ({ progress }) => (
    <div className="absolute inset-0 bg-white p-5 overflow-hidden">
      <div className="text-slate-500 text-xs mb-3 font-semibold tracking-wider uppercase">Install in 30 Seconds</div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { name: 'Gmail', color: 'bg-red-500' },
          { name: 'Outlook', color: 'bg-blue-500' },
          { name: 'Apple', color: 'bg-slate-700' },
          { name: 'Yahoo', color: 'bg-purple-500' },
        ].map((c, i) => {
          const isPicked = progress > 0.35 && i === 0;
          return (
            <div
              key={c.name}
              className={`rounded-lg p-2 border-2 text-center transition-all ${
                isPicked ? 'border-emerald-400 scale-105 shadow-md' : 'border-slate-200'
              }`}
            >
              <div className={`w-6 h-6 mx-auto rounded ${c.color} mb-1`} />
              <div className="text-[9px] font-semibold text-slate-700">{c.name}</div>
            </div>
          );
        })}
      </div>
      {progress > 0.5 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <div className="text-xs font-bold text-emerald-900">Gmail Setup — 3 Quick Steps</div>
          </div>
          <ol className="text-[11px] text-slate-700 space-y-1 list-decimal list-inside">
            <li>Click &quot;Copy Signature&quot;</li>
            <li>Open Gmail → Settings → Signature</li>
            <li>Paste & Save</li>
          </ol>
          {progress > 0.85 && (
            <div className="mt-2 flex items-center gap-1.5 text-emerald-700 text-[11px] font-semibold animate-in fade-in duration-300">
              <Check className="w-3 h-3" />
              Live in your inbox!
            </div>
          )}
        </div>
      )}
    </div>
  ),
};

// ===== NETWORKING DEMO =====
const NetworkingScenes = {
  Scene1_Search: ({ progress }) => (
    <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 to-slate-900 p-5 overflow-hidden">
      <div className="text-white/70 text-xs mb-3 font-semibold tracking-wider uppercase">Find Partners Near You</div>
      <div className="bg-white/10 rounded-lg p-2.5 mb-3 border border-white/20 flex items-center gap-2">
        <div className="w-4 h-4 rounded-full border-2 border-white/40" />
        <div className="text-white text-xs">
          {progress < 0.5 ? `Mortgage broker${progress > 0.1 ? 's' : ''}${progress > 0.2 ? ' in' : ''}${progress > 0.3 ? ' Austin' : ''}` : 'Mortgage brokers in Austin'}
          <span className="animate-pulse text-emerald-400">|</span>
        </div>
      </div>
      {progress > 0.5 && (
        <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
          {[
            { name: 'Michael Chen', match: 95, role: 'Mortgage Broker' },
            { name: 'Emily Rodriguez', match: 89, role: 'Insurance Agent' },
            { name: 'David Kim', match: 87, role: 'Financial Advisor' },
          ].map((p, i) => (
            <div
              key={p.name}
              className="bg-white/10 rounded-lg p-2.5 border border-white/15 flex items-center gap-2.5 animate-in fade-in slide-in-from-right duration-300"
              style={{ animationDelay: `${i * 150}ms` }}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-xs font-bold">
                {p.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-semibold">{p.name}</div>
                <div className="text-white/60 text-[10px]">{p.role}</div>
              </div>
              <div className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold border border-emerald-400/30">
                {p.match}%
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  ),

  Scene2_AIMatch: ({ progress }) => (
    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 p-5 overflow-hidden flex flex-col items-center justify-center">
      <div className="text-emerald-300 text-[10px] font-bold tracking-widest uppercase mb-3">AI Match Engine</div>
      <div className="relative flex items-center gap-6">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold shadow-xl">SJ</div>
        <div className="relative">
          <div className="w-2 h-2 rounded-full bg-emerald-400 absolute -top-1 left-0 animate-ping" />
          <div className="w-12 h-px bg-gradient-to-r from-amber-400 via-emerald-400 to-blue-500" />
          <Zap className="w-4 h-4 text-emerald-300 absolute -top-1.5 left-1/2 -translate-x-1/2 animate-pulse" />
        </div>
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white font-bold shadow-xl">MC</div>
      </div>
      <div className="mt-5 text-center">
        <div className="text-white font-bold text-lg font-['Outfit']">95% Match</div>
        <div className="text-emerald-200 text-[11px] mt-0.5">Realtor + Mortgage Broker = referral gold</div>
      </div>
      {progress > 0.6 && (
        <div className="mt-3 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-semibold animate-in fade-in zoom-in duration-500">
          Same zip · complementary services · 4.9★ ratings
        </div>
      )}
    </div>
  ),

  Scene3_Connect: ({ progress }) => (
    <div className="absolute inset-0 bg-white p-5 overflow-hidden">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white font-bold shrink-0">MC</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 text-sm">Michael Chen</div>
            <div className="text-blue-600 text-xs font-semibold">Mortgage Broker · HomeFirst</div>
            <div className="flex gap-1 mt-1">
              {[1,2,3,4,5].map(s => <Star key={s} className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />)}
              <span className="text-[10px] text-slate-500 ml-1">142 reviews</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <div className="bg-white/70 rounded p-1.5">
            <div className="text-sm font-bold text-slate-900">8yr</div>
            <div className="text-[9px] text-slate-500">Experience</div>
          </div>
          <div className="bg-white/70 rounded p-1.5">
            <div className="text-sm font-bold text-slate-900">$240M</div>
            <div className="text-[9px] text-slate-500">Closed</div>
          </div>
          <div className="bg-white/70 rounded p-1.5">
            <div className="text-sm font-bold text-emerald-600">95%</div>
            <div className="text-[9px] text-slate-500">Match</div>
          </div>
        </div>
      </div>
      <Button
        className={`w-full mt-3 gap-1.5 transition-all duration-500 ${
          progress > 0.6 ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        size="sm"
      >
        {progress > 0.6 ? (
          <>
            <CheckCircle className="w-3.5 h-3.5" />
            Connection Sent
          </>
        ) : (
          <>
            <UserPlus className="w-3.5 h-3.5" />
            Connect with Michael
          </>
        )}
      </Button>
      {progress > 0.3 && progress < 0.6 && (
        <MousePointer2
          className="absolute w-5 h-5 text-slate-700 drop-shadow-lg"
          style={{ left: '60%', top: '70%' }}
        />
      )}
    </div>
  ),

  Scene4_Refer: ({ progress }) => {
    const messages = [
      { from: 'them', text: 'Hey Sarah! Got a buyer pre-approved at $650k. Need a sharp realtor.' },
      { from: 'me',   text: 'Perfect timing — I have a new listing on Oak St. Sending over now 🎯' },
      { from: 'them', text: '🔥 Setting showing for Saturday. Cheers!' },
    ];
    const shownCount = Math.min(messages.length, Math.floor(progress * 3.5) + 1);
    return (
      <div className="absolute inset-0 bg-slate-50 p-4 overflow-hidden flex flex-col">
        <div className="text-slate-500 text-[10px] font-semibold tracking-wider uppercase mb-2">Live Referral Chat — Michael Chen</div>
        <div className="flex-1 space-y-2 overflow-hidden">
          {messages.slice(0, shownCount).map((m, i) => (
            <div
              key={i}
              className={`flex ${m.from === 'me' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-1 duration-300`}
            >
              <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-xs ${
                m.from === 'me'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
              }`}>
                {m.text}
              </div>
            </div>
          ))}
        </div>
        {progress > 0.85 && (
          <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex items-center gap-2 animate-in fade-in zoom-in duration-300">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <div className="text-[11px]">
              <span className="font-bold text-emerald-900">Referral closed:</span> <span className="text-emerald-700">$650k deal · commission earned</span>
            </div>
          </div>
        )}
      </div>
    );
  },
};

// ===== MARKETPLACE DEMO =====
const MarketplaceScenes = {
  Scene1_Browse: ({ progress }) => (
    <div className="absolute inset-0 bg-gradient-to-br from-amber-900 to-slate-900 p-5 overflow-hidden">
      <div className="text-amber-200 text-[10px] font-bold tracking-widest uppercase mb-3">Ad Marketplace</div>
      <div className="space-y-2">
        {[
          { host: 'Sarah Johnson', role: 'Realtor', sends: '12k/mo', price: '$45' },
          { host: 'David Kim', role: 'Financial Advisor', sends: '8k/mo', price: '$32' },
          { host: 'Lisa Thompson', role: 'CPA', sends: '5k/mo', price: '$22' },
        ].map((slot, i) => {
          const isHover = progress > 0.3 && progress < 0.65 && i === 0;
          const isPicked = progress > 0.65 && i === 0;
          return (
            <div
              key={slot.host}
              className={`rounded-lg p-2.5 border-2 transition-all duration-300 flex items-center gap-3 ${
                isPicked ? 'border-amber-400 bg-amber-400/10 scale-[1.02] shadow-lg shadow-amber-500/30' :
                isHover ? 'border-amber-300/60 bg-white/10' :
                'border-white/10 bg-white/5'
              }`}
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold">
                {slot.host.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-bold">{slot.host}</div>
                <div className="text-white/60 text-[10px]">{slot.role} · {slot.sends}</div>
              </div>
              <div className="text-right">
                <div className="text-amber-300 text-sm font-bold">{slot.price}</div>
                <div className="text-white/50 text-[9px]">/mo</div>
              </div>
            </div>
          );
        })}
      </div>
      {progress > 0.4 && progress < 0.65 && (
        <MousePointer2
          className="absolute w-5 h-5 text-white drop-shadow-lg"
          style={{ left: '78%', top: '32%' }}
        />
      )}
    </div>
  ),

  Scene2_Request: ({ progress }) => (
    <div className="absolute inset-0 bg-white p-5 overflow-hidden">
      <div className="text-slate-500 text-[10px] font-semibold tracking-wider uppercase mb-3">Request Placement</div>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 mb-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold">SJ</div>
          <div>
            <div className="text-xs font-bold text-slate-900">Sarah Johnson&apos;s Signature</div>
            <div className="text-[10px] text-amber-700">12,000 sends/mo · Real Estate audience</div>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <div>
          <div className="text-[10px] text-slate-500 mb-0.5">Your Ad Headline</div>
          <div className="border border-blue-300 bg-blue-50/50 rounded px-2 py-1.5 text-xs font-semibold text-slate-800">
            {`Low-rate mortgages for first-time buyers`.slice(0, Math.floor(progress * 50))}
            <span className="animate-pulse text-blue-500">|</span>
          </div>
        </div>
        {progress > 0.6 && (
          <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 gap-1.5 animate-in fade-in zoom-in duration-300">
            <Send className="w-3.5 h-3.5" />
            Send Request — $45/mo
          </Button>
        )}
      </div>
    </div>
  ),

  Scene3_Approve: ({ progress }) => (
    <div className="absolute inset-0 bg-slate-50 p-5 overflow-hidden">
      <div className="text-slate-500 text-[10px] font-semibold tracking-wider uppercase mb-3">Sarah&apos;s Inbox</div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
        <div className="flex items-start gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <Bell className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-slate-900">New ad request</div>
            <div className="text-[11px] text-slate-600 mt-0.5">Michael Chen wants to feature on your signature</div>
            <div className="text-[10px] text-emerald-600 font-bold mt-1">You earn $22.50/mo · 50% commission</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7"
          >
            Decline
          </Button>
          <Button
            size="sm"
            className={`text-xs h-7 transition-all duration-500 ${
              progress > 0.55 ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {progress > 0.55 ? (
              <span className="flex items-center gap-1"><Check className="w-3 h-3" />Approved</span>
            ) : (
              'Approve'
            )}
          </Button>
        </div>
      </div>
      {progress > 0.3 && progress < 0.55 && (
        <MousePointer2
          className="absolute w-5 h-5 text-slate-700 drop-shadow-lg"
          style={{ left: '70%', top: '62%' }}
        />
      )}
    </div>
  ),

  Scene4_Live: ({ progress }) => (
    <div className="absolute inset-0 bg-slate-100 p-4 overflow-hidden flex flex-col items-center justify-center">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 w-full max-w-sm">
        <div className="flex gap-3 items-start">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold shrink-0">SJ</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 text-sm">Sarah Johnson</div>
            <div className="text-amber-600 text-xs font-semibold">Real Estate Agent</div>
            <div className="text-slate-500 text-[10px]">Premier Realty</div>
          </div>
        </div>
        <div className="h-px bg-gradient-to-r from-amber-400 to-orange-500 my-2.5" />
        <div className="rounded-lg overflow-hidden border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-2.5 animate-in fade-in zoom-in duration-700">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">MC</div>
              <div className="min-w-0">
                <div className="text-[9px] text-amber-700 font-bold uppercase tracking-wide">Featured Partner</div>
                <div className="text-xs font-bold text-slate-900 truncate">Low-rate mortgages for first-time buyers</div>
                <div className="text-[10px] text-slate-600">Michael Chen · HomeFirst</div>
              </div>
            </div>
            <Megaphone className="w-4 h-4 text-amber-600 shrink-0" />
          </div>
        </div>
      </div>
      {progress > 0.4 && (
        <div className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-500">
          <CheckCircle className="w-4 h-4" />
          Live in 12,000 inboxes
          {progress > 0.7 && <span className="ml-1 text-emerald-100">· $22.50/mo earned</span>}
        </div>
      )}
    </div>
  ),
};

// ---------- Demo definitions ----------
const DEMOS = {
  signatures: {
    key: 'signatures',
    title: 'Professional Email Signatures',
    accent: 'from-blue-500 to-cyan-500',
    accentSolid: '#1f7ab3',
    icon: Mail,
    refTag: 'demo-signatures',
    totalDuration: 32000,
    scenes: [
      { duration: 5500, caption: 'Step 1 — Pick from 17 professional templates', callout: { text: 'Premium templates unlocked for Founding Members', x: '52%', y: '18%' }, Render: SignatureScenes.Scene1_Pick },
      { duration: 6500, caption: 'Step 2 — Add your info — that\'s it', Render: SignatureScenes.Scene2_Fill },
      { duration: 5000, caption: 'Step 3 — AI builds a conversion-optimized signature', callout: { text: 'Tuned to drive clicks & replies', x: '50%', y: '22%' }, Render: SignatureScenes.Scene3_AI },
      { duration: 7500, caption: 'Step 4 — Beautiful, branded, ready to send', Render: SignatureScenes.Scene4_Result },
      { duration: 7500, caption: 'Step 5 — Install in Gmail, Outlook, Apple Mail in under a minute', callout: { text: 'Step-by-step install wizard included', x: '50%', y: '78%' }, Render: SignatureScenes.Scene5_Install },
    ],
  },
  networking: {
    key: 'networking',
    title: 'Referral Networking',
    accent: 'from-emerald-500 to-teal-500',
    accentSolid: '#10b981',
    icon: Users,
    refTag: 'demo-networking',
    totalDuration: 32000,
    scenes: [
      { duration: 7500, caption: 'Step 1 — Search complementary pros in your area', Render: NetworkingScenes.Scene1_Search },
      { duration: 7500, caption: 'Step 2 — Our AI finds the perfect referral partners', callout: { text: 'Realtor + Loan Officer = closed deals', x: '50%', y: '12%' }, Render: NetworkingScenes.Scene2_AIMatch },
      { duration: 8000, caption: 'Step 3 — One tap to connect — they get notified instantly', Render: NetworkingScenes.Scene3_Connect },
      { duration: 9000, caption: 'Step 4 — Chat, refer, close — real deals from real partners', callout: { text: '$650k deal from one referral', x: '50%', y: '85%' }, Render: NetworkingScenes.Scene4_Refer },
    ],
  },
  marketplace: {
    key: 'marketplace',
    title: 'Marketplace & Advertising',
    accent: 'from-amber-500 to-orange-500',
    accentSolid: '#f59e0b',
    icon: DollarSign,
    refTag: 'demo-marketplace',
    totalDuration: 32000,
    scenes: [
      { duration: 8000, caption: 'Step 1 — Browse ad slots on other pros\' signatures', callout: { text: 'Targeted audiences · transparent pricing', x: '50%', y: '14%' }, Render: MarketplaceScenes.Scene1_Browse },
      { duration: 7500, caption: 'Step 2 — Request placement with your headline', Render: MarketplaceScenes.Scene2_Request },
      { duration: 7500, caption: 'Step 3 — The host approves — you both earn', callout: { text: 'Host earns 50% commission', x: '50%', y: '14%' }, Render: MarketplaceScenes.Scene3_Approve },
      { duration: 9000, caption: 'Step 4 — Your ad goes live in thousands of inboxes', Render: MarketplaceScenes.Scene4_Live },
    ],
  },
};

// ---------- Modal Component ----------
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate a stable per-session id (one per page load, not per modal open) so the
// same anonymous viewer can be correlated across the 3 demos.
let __demoSessionId = null;
const getDemoSessionId = () => {
  if (__demoSessionId) return __demoSessionId;
  try {
    const stored = sessionStorage.getItem('cm_demo_sid');
    if (stored) { __demoSessionId = stored; return stored; }
    const sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('cm_demo_sid', sid);
    __demoSessionId = sid;
    return sid;
  } catch {
    return `s_${Math.random().toString(36).slice(2, 10)}`;
  }
};

const logDemoEvent = (event, demoKey, extra = {}) => {
  if (!BACKEND_URL) return;
  const payload = {
    event,
    demo_key: demoKey,
    session_id: getDemoSessionId(),
    ...extra,
  };
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    // Use sendBeacon when available so close-early fires reliably on unmount
    if (navigator.sendBeacon && navigator.sendBeacon(`${BACKEND_URL}/api/analytics/demo-event`, blob)) {
      return;
    }
  } catch { /* fall through */ }
  fetch(`${BACKEND_URL}/api/analytics/demo-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
};

export default function DemoVideoModal({ demoKey, onClose }) {
  const demo = DEMOS[demoKey];
  const [elapsed, setElapsed] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const rafRef = useRef(null);
  const lastTickRef = useRef(null);
  const elapsedRef = useRef(0);
  const completedRef = useRef(false);
  const ctaClickedRef = useRef(false);

  // Fire "opened" once on mount
  useEffect(() => {
    logDemoEvent('opened', demoKey);
    logFunnel('demo_opened', { demo_key: demoKey });
    // On unmount: if user closed before completion AND didn't click CTA, log closed_early
    return () => {
      if (!completedRef.current && !ctaClickedRef.current) {
        logDemoEvent('closed_early', demoKey, {
          seconds_watched: Math.round(elapsedRef.current / 100) / 10,
        });
      }
    };
  }, [demoKey]);

  // Keep elapsedRef in sync
  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying) return;
    lastTickRef.current = performance.now();
    const tick = (now) => {
      const dt = now - lastTickRef.current;
      lastTickRef.current = now;
      setElapsed(prev => {
        const next = prev + dt;
        if (next >= demo.totalDuration) {
          setIsPlaying(false);
          if (!completedRef.current) {
            completedRef.current = true;
            logDemoEvent('completed', demoKey);
            logFunnel('demo_completed', { demo_key: demoKey });
          }
          return demo.totalDuration;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, demo.totalDuration]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Compute current scene
  let cum = 0;
  let sceneIndex = 0;
  let sceneStart = 0;
  for (let i = 0; i < demo.scenes.length; i++) {
    const s = demo.scenes[i];
    if (elapsed < cum + s.duration) {
      sceneIndex = i;
      sceneStart = cum;
      break;
    }
    cum += s.duration;
    sceneIndex = i;
    sceneStart = cum - s.duration;
  }
  const scene = demo.scenes[sceneIndex];
  const sceneElapsed = elapsed - sceneStart;
  const sceneProgress = Math.min(1, sceneElapsed / scene.duration);
  const SceneRender = scene.Render;
  const overallProgress = elapsed / demo.totalDuration;
  const isFinished = elapsed >= demo.totalDuration;

  const replay = () => {
    setElapsed(0);
    completedRef.current = false;
    setIsPlaying(true);
  };

  const handleCtaClick = () => {
    if (!ctaClickedRef.current) {
      ctaClickedRef.current = true;
      logDemoEvent('cta_clicked', demoKey, { ref_code: demo.refTag });
    }
    onClose();
  };

  const Icon = demo.icon;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-md p-3 sm:p-6 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="demo-video-modal-overlay"
    >
      <div
        className="relative bg-slate-950 rounded-2xl shadow-2xl border border-white/10 w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`demo-video-modal-${demo.key}`}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-slate-900/80">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${demo.accent} flex items-center justify-center shrink-0`}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-white text-sm font-bold font-['Outfit'] truncate">{demo.title}</div>
              <div className="text-white/40 text-[10px]">30-second product demo</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors shrink-0"
            data-testid="demo-video-modal-close"
            aria-label="Close demo"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Player */}
        <div
          className="relative aspect-[4/3] sm:aspect-[16/10] bg-slate-900 overflow-hidden"
          data-testid="demo-video-player"
        >
          {/* Active scene */}
          <SceneRender progress={sceneProgress} />

          {/* Floating callout */}
          {scene.callout && sceneProgress > 0.3 && sceneProgress < 0.9 && (
            <div
              className="absolute z-20 pointer-events-none animate-in fade-in slide-in-from-bottom-2 duration-500"
              style={{ left: scene.callout.x, top: scene.callout.y, transform: 'translate(-50%, -50%)' }}
            >
              <div className="relative bg-white text-slate-900 text-[11px] font-semibold px-3 py-1.5 rounded-lg shadow-2xl border border-slate-200 whitespace-nowrap max-w-[80vw]">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-amber-500 shrink-0" />
                  <span className="truncate">{scene.callout.text}</span>
                </div>
              </div>
            </div>
          )}

          {/* Bottom subtitle bar */}
          <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-transparent p-4 pt-10">
            <div className="text-white text-xs sm:text-sm font-semibold leading-snug" data-testid="demo-video-caption">
              {scene.caption}
            </div>
          </div>

          {/* Mute / Replay controls top-right */}
          <div className="absolute top-3 right-3 z-30 flex gap-2">
            <button
              onClick={() => setIsMuted(m => !m)}
              className="w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white transition-colors border border-white/10"
              data-testid="demo-video-mute-btn"
              aria-label="Toggle mute"
            >
              {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={replay}
              className="w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white transition-colors border border-white/10"
              data-testid="demo-video-replay-btn"
              aria-label="Replay demo"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Muted indicator badge — shown briefly at start */}
          {isMuted && elapsed < 3500 && (
            <div className="absolute bottom-16 left-3 z-30 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white/80 text-[10px] font-semibold border border-white/10 flex items-center gap-1 animate-in fade-in duration-300">
              <VolumeX className="w-3 h-3" />
              Muted · captions on
            </div>
          )}

          {/* End-of-video overlay with CTA */}
          {isFinished && (
            <div
              className="absolute inset-0 z-40 bg-slate-950/85 backdrop-blur-sm flex flex-col items-center justify-center p-5 animate-in fade-in duration-500"
              data-testid="demo-video-end-overlay"
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${demo.accent} flex items-center justify-center shadow-xl mb-3`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <div className="text-white text-lg sm:text-xl font-bold font-['Outfit'] text-center mb-1.5">
                Ready to claim your Founding Member spot?
              </div>
              <div className="text-white/60 text-xs sm:text-sm text-center mb-4 max-w-sm">
                First 1,000 members get premium free for life — no credit card, no catch.
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
                <Link to={`/register?ref=${demo.refTag}`} className="flex-1" onClick={handleCtaClick}>
                  <Button className={`w-full bg-gradient-to-r ${demo.accent} text-white hover:opacity-90 gap-1.5 font-semibold`} data-testid={`demo-cta-signup-${demo.key}`}>
                    Sign Up Free
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="border-white/20 bg-white/5 hover:bg-white/10 text-white gap-1.5"
                  onClick={replay}
                  data-testid={`demo-cta-replay-${demo.key}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Replay
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/5 w-full">
          <div
            className={`h-full bg-gradient-to-r ${demo.accent} transition-all duration-100`}
            style={{ width: `${overallProgress * 100}%` }}
          />
        </div>

        {/* Bottom CTA bar (always visible) */}
        <div className="bg-slate-900/80 border-t border-white/10 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-white/70 text-[11px] sm:text-xs hidden sm:block">
            Ready to claim your Founding Member spot?
          </div>
          <div className="text-white/70 text-[11px] sm:hidden">
            Founding Member spot?
          </div>
          <Link to={`/register?ref=${demo.refTag}`} onClick={handleCtaClick}>
            <Button
              size="sm"
              className="bg-gradient-to-r from-emerald-500 to-green-500 hover:opacity-90 text-white gap-1.5 font-semibold shrink-0"
              data-testid={`demo-cta-footer-${demo.key}`}
            >
              Sign Up Free
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
